/**
 * Site-wide feature switches.
 *
 * Things the client wants held back until the business is ready for them, kept
 * in one place so they can be turned on later without hunting through the app.
 */
export const FEATURES = {
  /**
   * CPH capture and the "cross-checked with APHA" claims.
   *
   * OFF for now at the client's request: we are not collecting County Parish
   * Holding numbers and we are not checking anything against APHA records, so
   * saying otherwise on the site would be a claim we cannot stand behind.
   *
   * Setting this to true restores: the CPH field on registration, the CPH field
   * on the profile and post-listing forms, the "CPH verified" filter on the
   * marketplace, the CPH row on listing pages, and the APHA wording throughout.
   * The database columns (profiles.cph_number, profiles.cph_verified) and the
   * admin verify toggle stay in place the whole time, so nothing is lost.
   */
  cphVerification: false,
} as const;

/**
 * Display name for the third marketplace category — everything that isn't
 * livestock or machinery (tools, feed, fencing, trailers, spares).
 *
 * The client asked for "Deadstock/Farm Equipment" to cover both bases: the trade
 * term farmers scan for, and the plain-English one newcomers understand. The
 * underlying category value stays 'farm-equipment', so existing links and rows
 * are unaffected.
 */
export const FARM_EQUIPMENT_LABEL = 'Deadstock/Farm Equipment'

/**
 * Display name for the arable/forage sector.
 *
 * Was "Crops & Feed", which the client changed to "Grain, Feed & Bedding" — the
 * words the trade actually uses, and it stops bedding looking like an
 * afterthought. The stored category value stays 'crops-feed' so existing rows
 * and links keep working; only the label moves.
 */
export const GRAIN_FEED_LABEL = 'Grain, Feed & Bedding'

/**
 * Wording used where the site used to promise CPH/APHA checks. Keeps the
 * verification story honest while the checks are limited to identity and
 * business details.
 */
export const VERIFICATION_COPY = FEATURES.cphVerification
  ? {
      short: 'CPH verified',
      badgeLine: 'CPH numbers verified with APHA',
      members: 'CPH Verified Members',
      filterHint: 'Show only CPH-verified members',
    }
  : {
      short: 'ID verified',
      badgeLine: 'Every seller identity checked before listing',
      members: 'Verified Members',
      filterHint: 'Show only verified members',
    };
